/**
 * PhotoGrid - Displays a grid of photos with thumbnails, captions, and optional
 * delete/add controls. Used in both work order and inspection detail pages to
 * show before/after photos and observation/correction photos.
 *
 * Now integrates the PhotoCapture component directly: when `editable` is true,
 * the "Add Photo" card is replaced by an inline PhotoCapture button that opens
 * the device camera (mobile) or file picker (desktop). Captured photos are
 * passed to the `onPhotoCaptured` callback for upload or offline queuing.
 *
 * Uploads happen in the background, so the grid also renders `pendingPhotos` —
 * optimistic tiles showing the local preview with an UploadStatusChip over it.
 * The tech sees the photo land in the grid immediately and can carry on; the
 * chip tracks it to the server and offers a retry if the connection drops. Each
 * optimistic tile is replaced by the real, server-backed photo once the parent
 * refetches.
 *
 * Layout: 2-column grid on all screen sizes with responsive gap spacing.
 * Each cell shows a square thumbnail image with a caption below it.
 * When `editable` is true, each photo gets a delete (X) button overlay, and
 * the PhotoCapture component appears at the end of the grid.
 *
 * @example
 * <PhotoGrid
 *	 photos={workOrder.photos}
 *	 photoTypeLabel="Before Photos"
 *	 editable={true}
 *	 onPhotoCaptured={handlePhotoCaptured}
 *	 pendingPhotos={queue.items}
 *	 onRetryUpload={queue.retry}
 *	 onDelete={handleDeletePhoto}
 * />
 */
import React from 'react';
import { PhotoCapture } from '../PhotoCapture';
import type { CapturedPhoto } from '../PhotoCapture';
import { UploadStatusChip } from '../../atoms';
import type { PhotoUploadStatus } from '../../../hooks/usePhotoUploadQueue';

/* ============================================
 * TYPE DEFINITIONS
 * ============================================ */

/** Generic photo shape accepted by the grid */
export interface PhotoItem {
	/** Unique identifier for the photo */
	id: string;
	/** Full URL to the image file */
	url: string;
	/** Optional caption text displayed below the thumbnail */
	caption: string;
}

/**
 * A photo that is on its way to the server but isn't persisted yet. Rendered
 * from the caller's usePhotoUploadQueue entries.
 */
export interface PendingPhotoItem {
	/** Queue entry id — used as the React key and for retry/dismiss. */
	id: string;
	/** Local data URL for the preview; null if none could be produced. */
	previewUrl: string | null;
	status: PhotoUploadStatus;
	error?: string | null;
}

/**
 * Props for the PhotoGrid component.
 *
 * @property photos			  - Array of photo objects to display
 * @property photoTypeLabel	  - Label shown as the section heading (e.g., "Before Photos")
 * @property editable		  - When true, shows delete buttons and the PhotoCapture component
 * @property onAdd			  - Legacy callback fired when the user clicks "Add Photo" (fallback)
 * @property onPhotoCaptured  - New callback fired with CapturedPhoto data from PhotoCapture.
 *								When provided, replaces the legacy onAdd button with the
 *								integrated camera/file picker experience.
 * @property pendingPhotos	  - In-flight background uploads, rendered as optimistic tiles
 * @property onRetryUpload	  - Retry a failed background upload, by queue entry id
 * @property onDismissUpload  - Give up on a failed upload and drop the tile
 * @property onDelete		  - Callback fired with the photo ID when delete is clicked
 */
export interface PhotoGridProps {
	photos: PhotoItem[];
	photoTypeLabel?: string;
	editable?: boolean;
	onAdd?: () => void;
	onPhotoCaptured?: (photo: CapturedPhoto) => void;
	pendingPhotos?: PendingPhotoItem[];
	onRetryUpload?: (id: string) => void;
	onDismissUpload?: (id: string) => void;
	onDelete?: (photoId: string) => void;
	/** Optional outer className merged onto the root element. */
	className?: string;
}

const PhotoGrid: React.FC<PhotoGridProps> = ({ photos, photoTypeLabel, editable = false, onAdd, onPhotoCaptured, pendingPhotos = [], onRetryUpload, onDismissUpload, onDelete, className }) => {
	return (
		<div className={['photo-grid', className ?? ''].join(' ')} data-ref="organism-photo-grid">
			{/* Optional section heading label. In-flight uploads are counted so
				the number doesn't jump around as they land. */}
			{photoTypeLabel && (
				<h3 className="photo-grid__title text-14px font-sans font-semibold text-brand-text-muted mb-12px">
					{photoTypeLabel} ({photos.length + pendingPhotos.length})
				</h3>
			)}

			{/* 2-column responsive grid with consistent gap spacing */}
			<div className="photo-grid__grid grid grid-cols-2 gap-12px">
				{/* Render each photo as a card with thumbnail and caption */}
				{photos.map((photo) => (
					<div key={photo.id} className="photo-grid__item relative group rounded-lg overflow-hidden border border-brand-border bg-brand-surface">
						{/* Photo thumbnail — object-cover ensures consistent sizing
							regardless of original image dimensions */}
						<div className="aspect-square overflow-hidden">
							<img src={photo.url} alt={photo.caption || 'Photo'} className="w-full h-full object-cover" loading="lazy" />
						</div>

						{/* Caption text below the image */}
						{photo.caption && (
							<div className="px-8px py-6px">
								<p className="text-12px font-sans text-brand-text-muted line-clamp-2">
									{photo.caption}
								</p>
							</div>
						)}

						{/* Delete button overlay — only shown in editable mode.
							Appears on hover (desktop) and is always visible on touch devices
							via the group-hover class. Positioned at top-right corner. */}
						{editable && onDelete && (
							<button type="button" onClick={() => onDelete(photo.id)} className="absolute top-4px right-4px opacity-100 tablet:opacity-0 tablet:group-hover:opacity-100 transition-opacity bg-red-600 text-white rounded-full w-44px h-44px desktop:w-36px desktop:h-36px flex items-center justify-center shadow-md hover:bg-red-700" aria-label={`Delete photo ${photo.caption || photo.id}`}>
								{/* X icon */}
								<svg className="h-16px w-16px" fill="none" viewBox="0 0 24 24" strokeWidth={2.5} stroke="currentColor" aria-hidden="true">
									<path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
								</svg>
							</button>
						)}
					</div>
				))}

				{/* Optimistic tiles for photos still uploading. They sit after the
					persisted photos and disappear once the parent's refetch brings back
					the real record, so the grid never appears to lose a photo in the
					handoff. The preview is dimmed while in flight so it reads as "not
					final yet" without hiding what was captured. */}
				{pendingPhotos.map((pending) => (
					<div key={pending.id} className="photo-grid__item photo-grid__item--pending relative rounded-lg overflow-hidden border border-brand-border bg-brand-surface">
						<div className="aspect-square overflow-hidden bg-gray-100 flex items-center justify-center">
							{pending.previewUrl ? (
								<img src={pending.previewUrl} alt="Uploading" className={['w-full h-full object-cover transition-opacity', pending.status === 'uploaded' ? 'opacity-100' : 'opacity-60'].join(' ')} />
							) : (
								/* No decodable preview (e.g. a HEIC this browser cannot
									render) — a neutral glyph beats a broken-image icon. */
								<svg className="h-32px w-32px text-gray-400" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" aria-hidden="true">
									<path strokeLinecap="round" strokeLinejoin="round" d="m2.25 15.75 5.159-5.159a2.25 2.25 0 0 1 3.182 0l5.159 5.159m-1.5-1.5 1.409-1.409a2.25 2.25 0 0 1 3.182 0l2.909 2.909M18 6.75h.008v.008H18V6.75Z" />
								</svg>
							)}
						</div>

						{/* Status chip — the whole point of the optimistic tile. */}
						<div className="photo-grid__item-status absolute bottom-6px left-6px right-6px flex items-center justify-between gap-4px">
							<UploadStatusChip status={pending.status} error={pending.error} onRetry={pending.status === 'failed' && onRetryUpload ? () => onRetryUpload(pending.id) : undefined} />

							{/* Escape hatch: an upload that keeps failing can be dismissed
								so it stops occupying a slot in the grid. */}
							{pending.status === 'failed' && onDismissUpload && (
								<button type="button" onClick={() => onDismissUpload(pending.id)} className="relative w-24px h-24px rounded-full bg-black/60 text-white flex items-center justify-center hover:bg-black/80 flex-shrink-0 before:absolute before:-inset-10px before:content-['']" aria-label="Discard this failed upload" title="Discard">
									<svg className="h-12px w-12px" fill="none" viewBox="0 0 24 24" strokeWidth={2.5} stroke="currentColor" aria-hidden="true">
										<path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
									</svg>
								</button>
							)}
						</div>
					</div>
				))}

				{/* Add photo area — rendered in editable mode.
					Two modes of operation:
					1. If onPhotoCaptured is provided, renders the inline PhotoCapture component
					   with camera, file upload, and drag-and-drop support.
					2. Falls back to the legacy onAdd button for backward compatibility. */}
				{editable && onPhotoCaptured && (
					<div className="col-span-2">
						{/* Integrated PhotoCapture — camera, file upload, and drag-and-drop.
							Sizing is left to the shared compressPhoto defaults so every
							capture surface produces identically-sized uploads. */}
						<PhotoCapture onCapture={onPhotoCaptured} label="Add Photo" />
					</div>
				)}

				{/* Legacy "Add Photo" button — only shown when onPhotoCaptured is NOT provided
					but the legacy onAdd callback IS provided. Maintains backward compatibility
					with existing usages that haven't migrated to the new PhotoCapture flow. */}
				{editable && !onPhotoCaptured && onAdd && (
					<button type="button" onClick={onAdd} className="aspect-square rounded-lg border-2 border-dashed border-brand-border flex flex-col items-center justify-center gap-8px text-brand-text-muted hover:border-brand-primary hover:text-brand-primary transition-colors bg-brand-surface" aria-label="Add photo">
						{/* Plus icon */}
						<svg className="h-32px w-32px" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" aria-hidden="true">
							<path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
						</svg>
						<span className="text-14px font-sans font-medium">Add Photo</span>
					</button>
				)}
			</div>

			{/* Empty state when no photos exist and not in editable mode */}
			{photos.length === 0 && !editable && (
				<p className="text-14px font-sans text-brand-text-muted text-center py-24px">
					No photos available.
				</p>
			)}
		</div>
	);
};

export default PhotoGrid;
